import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

/**
 * Schedule Builder
 *
 * A "webbed" canvas where the school sits at the top and connects down to
 * its enrollment years, which connect to their tracks, learning periods, and
 * exceptions. Each node exposes hover affordances ("+", edit, delete) so the
 * hierarchy can be grown intuitively. The canvas supports mouse-wheel zoom
 * and click-and-drag pan.
 *
 * Backed by /api/schedule-builder/periods (see api/scheduleBuilder.js).
 */

const WEEKDAYS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' }
];

const TYPE_LABEL = {
  enrollment: 'Enrollment year',
  track: 'Track',
  learning: 'Learning period',
  exception_remove: 'Exception (remove)',
  exception_add: 'Exception (add)'
};

const SHORT_TYPE_LABEL = {
  enrollment: 'Enrollment',
  track: 'Track',
  learning: 'Learning',
  exception_remove: 'Exc. remove',
  exception_add: 'Exc. add'
};

const TYPE_ORDER = ['enrollment', 'track', 'learning', 'exception_remove', 'exception_add'];

const TYPE_COLOR = {
  school: '#0f172a',
  enrollment: '#1d4ed8',
  track: '#047857',
  learning: '#b45309',
  exception_remove: '#9333ea',
  exception_add: '#0891b2'
};

const ALLOWED_CHILDREN = {
  school: ['enrollment'],
  enrollment: ['track', 'learning', 'exception_remove', 'exception_add'],
  track: ['learning', 'exception_remove', 'exception_add'],
  learning: ['exception_remove', 'exception_add'],
  exception_remove: [],
  exception_add: []
};

// ── Exception "scope" — restricts an exception to a single role.
// Used by getDayState (forRole filter) and rendered as a small badge on the
// exception node card so reviewers can tell at a glance that a holiday is,
// e.g., a "Student-only PD day" (staff still works, SLA timers don't pause).
const SCOPE_OPTIONS = [
  { id: 'all', label: 'Everyone', short: 'All' },
  { id: 'students', label: 'Students only', short: 'Students' },
  { id: 'staff', label: 'Staff only', short: 'Staff' }
];
const SCOPE_LABEL = {
  all: 'Everyone',
  students: 'Students only',
  staff: 'Staff only'
};
const SCOPE_COLOR = {
  all: '#64748b',
  students: '#dc2626',
  staff: '#b45309'
};

const NODE_W = 208;
const NODE_H = 76;
const H_GAP = 28;
const V_GAP = 96;

const SCHOOL_ID = '__school__';
const CANVAS_TOP_INSET = 56; // clearance for floating school picker toolbar

// System grab/grabbing cursors can render as a faint white hand on light backgrounds.
const PAN_HAND_PATH =
  'M8 11V8a2 2 0 1 1 4 0v1M12 11V7a2 2 0 1 1 4 0v4M16 11V8a2 2 0 1 1 4 0v6a8 8 0 0 1-16 0v-3a2 2 0 1 1 4 0';

function panCursorSvg(stroke) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="${PAN_HAND_PATH}" stroke="#ffffff" stroke-width="3.25" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="${PAN_HAND_PATH}" stroke="${stroke}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

function panCursor(stroke, fallback, hotspot = 12) {
  return `url("data:image/svg+xml,${encodeURIComponent(panCursorSvg(stroke))}") ${hotspot} ${hotspot}, ${fallback}`;
}

const PAN_GRAB_CURSOR = panCursor('#0f172a', 'grab');
const PAN_GRABBING_CURSOR = panCursor('#1d4ed8', 'grabbing');

// ─────────────────────────────────────────────────────────────────────────────
// Utilities

function defaultCard(userData) {
  if (!userData?.accesscards || !userData.default_accesscard_id) return null;
  return userData.accesscards.find((c) => c.accesscard_id === userData.default_accesscard_id) || null;
}

function isGlobalScope(card) {
  if (!card) return false;
  return (
    card.global ||
    card.school_id == null ||
    ['Super - Admin', 'System Admin', 'Super - System Admin', 'Owner'].includes(card.role)
  );
}

function childrenByParent(periods) {
  const map = new Map();
  map.set('__root__', []);
  for (const p of periods) {
    const key = p.parent_id || '__root__';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  for (const [, arr] of map) {
    arr.sort((a, b) => {
      const oa = TYPE_ORDER.indexOf(a.type);
      const ob = TYPE_ORDER.indexOf(b.type);
      if (oa !== ob) return oa - ob;
      const sa = a.sort_order ?? 999999;
      const sb = b.sort_order ?? 999999;
      if (sa !== sb) return sa - sb;
      return String(a.start_date).localeCompare(String(b.start_date));
    });
  }
  return map;
}

/**
 * Build a positioned tree of nodes from a flat period list, with a virtual
 * `__school__` root at depth 0. Returns { nodes, edges, bounds }.
 */
function buildLayout(periods, schoolName) {
  const childMap = childrenByParent(periods);
  const roots = childMap.get('__root__') || [];

  // Bottom-up: compute subtree pixel width
  const buildSubtree = (period) => {
    const kids = (childMap.get(period.period_id) || []).map(buildSubtree);
    const childrenWidth = kids.length
      ? kids.reduce((s, k) => s + k.width, 0) + (kids.length - 1) * H_GAP
      : 0;
    const width = Math.max(NODE_W, childrenWidth);
    return { period, kids, width };
  };

  const subtrees = roots.map(buildSubtree);
  const subtreeTotal = subtrees.length
    ? subtrees.reduce((s, t) => s + t.width, 0) + (subtrees.length - 1) * H_GAP
    : NODE_W;
  const schoolWidth = Math.max(NODE_W, subtreeTotal);

  // Top-down: place each subtree, parent centered over its children
  const nodes = [];
  const edges = [];

  const schoolCx = schoolWidth / 2;
  const schoolY = 0;
  nodes.push({
    id: SCHOOL_ID,
    type: 'school',
    period: null,
    name: schoolName || 'School',
    x: schoolCx,
    y: schoolY
  });

  const placeSubtree = (subtree, leftX, y, parentCenter) => {
    const cx = leftX + subtree.width / 2;
    nodes.push({
      id: subtree.period.period_id,
      type: subtree.period.type,
      period: subtree.period,
      name: subtree.period.name,
      x: cx,
      y
    });
    if (parentCenter) {
      edges.push({
        from: parentCenter,
        to: { x: cx, y },
        type: subtree.period.type
      });
    }
    if (!subtree.kids.length) return;
    const kidsTotal =
      subtree.kids.reduce((s, k) => s + k.width, 0) + (subtree.kids.length - 1) * H_GAP;
    let cursor = cx - kidsTotal / 2;
    for (const kid of subtree.kids) {
      placeSubtree(kid, cursor, y + NODE_H + V_GAP, { x: cx, y });
      cursor += kid.width + H_GAP;
    }
  };

  let cursor = schoolCx - subtreeTotal / 2;
  for (const st of subtrees) {
    placeSubtree(st, cursor, NODE_H + V_GAP, { x: schoolCx, y: schoolY });
    cursor += st.width + H_GAP;
  }

  // Compute bounds (with padding) so we can size the inner stage and center the view
  const padX = 80;
  const padY = 60;
  let maxY = 0;
  for (const n of nodes) {
    if (n.y > maxY) maxY = n.y;
  }
  const bounds = {
    width: schoolWidth + padX * 2,
    height: maxY + NODE_H + padY * 2,
    rootX: schoolCx + padX,
    rootY: padY
  };
  // Shift everything by padX, padY so the stage origin is (0,0)
  for (const n of nodes) {
    n.x += padX;
    n.y += padY;
  }
  for (const e of edges) {
    e.from.x += padX;
    e.from.y += padY;
    e.to.x += padX;
    e.to.y += padY;
  }
  return { nodes, edges, bounds };
}

const formatDateRange = (period) => {
  if (!period?.start_date) return '';
  const s = String(period.start_date).slice(5, 10); // MM-DD
  const e = period.end_date ? String(period.end_date).slice(5, 10) : '';
  return e ? `${s} → ${e}` : s;
};

const formatYear = (period) => {
  if (!period?.start_date) return '';
  return String(period.start_date).slice(0, 4);
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const truncate = (s, n) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

// ─────────────────────────────────────────────────────────────────────────────
// Calendar helpers (local-time, no timezone shenanigans)

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAY_HEADER = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // Sun → Sat

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function isoWeekday(d) {
  // JS getDay: 0=Sun..6=Sat → ISO 1=Mon..7=Sun
  const w = d.getDay();
  return w === 0 ? 7 : w;
}

function enumerateMonths(start, end) {
  const months = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

/**
 * Decide a per-day status for a base period (track or LP) considering its
 * effective exceptions. Returns 'session' | 'added' | 'removed' | 'off'.
 *
 * Priority rule when multiple exceptions apply on the same day:
 *   priority = (10 − depth) × 100 + sort_order
 *     · depth 0  → exceptions that are direct children of the period being
 *       computed (most specific). Base score 1000.
 *     · depth 1+ → inherited from ancestors. Base score decreases by 100/level.
 *     · sort_order acts as a "priority boost": +100 promotes one cascade level.
 *   Highest priority wins; tiebreaker = remove beats add (more conservative).
 *
 * Optional `forRole`:
 *   'students' → ignore exceptions with scope='staff'    (those don't affect students)
 *   'staff'    → ignore exceptions with scope='students' (those don't affect staff)
 *   null/'all' → include every exception (single-perspective view)
 *
 * In the schedule preview we default to 'students' because tracks/LPs are
 * student-attendance calendars. Workflow SLAs for staff actions should call
 * this with forRole='staff' so student-only PD days don't pause the timer.
 */
function getDayState(date, baseMeetingDays, exceptions, forRole = null) {
  return getDayDetail(date, baseMeetingDays, exceptions, forRole).state;
}

/**
 * Same logic as getDayState but returns both the day state AND the winning
 * exception's scope (or 'all' if no exception applied). The calendar preview
 * uses this so it can stamp a small icon on cells where the winner is a
 * role-scoped exception — telling reviewers "this day is off for students
 * only; staff still work."
 */
function getDayDetail(date, baseMeetingDays, exceptions, forRole = null) {
  const wd = isoWeekday(date);
  const baseDays =
    baseMeetingDays && baseMeetingDays.length > 0 ? baseMeetingDays : [1, 2, 3, 4, 5, 6, 7];
  const isBaseSession = baseDays.includes(wd);

  const applicable = [];
  for (const ex of exceptions || []) {
    if (!ex.start || !ex.end) continue;
    if (date < ex.start || date > ex.end) continue;
    const exDays = ex.meeting_days && ex.meeting_days.length > 0 ? ex.meeting_days : null;
    if (exDays && !exDays.includes(wd)) continue;
    if (forRole && forRole !== 'all' && ex.scope && ex.scope !== 'all' && ex.scope !== forRole) {
      continue;
    }
    applicable.push(ex);
  }

  if (applicable.length === 0) {
    return { state: isBaseSession ? 'session' : 'off', scope: 'all' };
  }

  const priorityOf = (e) => (10 - (e.depth ?? 0)) * 100 + (e.sort_order ?? 0);
  applicable.sort((a, b) => {
    const ap = priorityOf(a);
    const bp = priorityOf(b);
    if (ap !== bp) return bp - ap;
    if (a.type !== b.type) return a.type === 'exception_remove' ? -1 : 1;
    return 0;
  });

  const winner = applicable[0];
  const winnerScope = winner.scope || 'all';
  if (winner.type === 'exception_remove') {
    return { state: isBaseSession ? 'removed' : 'off', scope: winnerScope };
  }
  return { state: isBaseSession ? 'session' : 'added', scope: winnerScope };
}

const STATE_COLOR = {
  session: { bg: '#dcfce7', border: '#86efac', text: '#166534' }, // green
  added:   { bg: '#fef9c3', border: '#fde047', text: '#854d0e' }, // yellow
  removed: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' }, // red
  off:     { bg: '#ffffff', border: '#f1f5f9', text: '#cbd5e1' }
};

/**
 * Walk up the parent chain and collect every exception period (add/remove)
 * that is a direct child of any ancestor. These are "inherited" exceptions
 * that cascade DOWN: e.g. an enrollment-level holiday applies to all of its
 * tracks and their learning periods.
 *
 * Returns { raw, depth } pairs where depth = how many levels up the
 * exception's parent is from the queried period (1 = parent, 2 = grandparent, …).
 */
function collectAncestorExceptions(period, allPeriods) {
  if (!period) return [];
  const out = [];
  let cur = period;
  let depth = 1;
  const seen = new Set();
  while (cur && cur.parent_id) {
    if (seen.has(cur.parent_id)) break; // cycle guard
    seen.add(cur.parent_id);
    const parent = allPeriods.find((p) => p.period_id === cur.parent_id);
    if (!parent) break;
    for (const p of allPeriods) {
      if (
        p.parent_id === parent.period_id &&
        (p.type === 'exception_add' || p.type === 'exception_remove')
      ) {
        out.push({ raw: p, depth });
      }
    }
    cur = parent;
    depth += 1;
  }
  return out;
}

function shapeExceptionRow(raw, depth = 0) {
  return {
    period_id: raw.period_id,
    parent_id: raw.parent_id,
    name: raw.name,
    type: raw.type,
    start: parseLocalDate(raw.start_date),
    end: parseLocalDate(raw.end_date),
    meeting_days: raw.meeting_days || [],
    sort_order: raw.sort_order || 0,
    scope: raw.scope || 'all',
    depth
  };
}

/**
 * Build the full exception set that applies to `period`: own direct
 * exception children (depth 0) plus ancestor-level exceptions (depth 1+).
 * Used by both the schedule preview and the conflict analyzer.
 */
function effectiveExceptionsFor(period, allPeriods) {
  if (!period) return [];
  const ownPairs = (allPeriods || [])
    .filter(
      (p) =>
        p.parent_id === period.period_id &&
        (p.type === 'exception_add' || p.type === 'exception_remove')
    )
    .map((p) => ({ raw: p, depth: 0 }));
  const inherited = collectAncestorExceptions(period, allPeriods || []);
  return [...ownPairs, ...inherited]
    .map(({ raw, depth }) => shapeExceptionRow(raw, depth))
    .filter((p) => p.start && p.end);
}

/**
 * Analyze the impact of a proposed exception (new or edited) on the existing
 * schedule. Returns:
 *   - `overlapping`: descendant exceptions whose date+meeting_days overlap
 *     this one (each tagged with whether it currently wins or loses against
 *     the proposed exception under the priority rule).
 *   - `gainedSessionDays` / `lostSessionDays`: net change across all
 *     schedulable descendants (track / learning) of the proposed exception's
 *     parent, comparing "saved state" vs "form state".
 *   - `requiredPriorityToWin`: smallest sort_order this proposed exception
 *     would need so that it beats every overlapping conflict (informational).
 */
function analyzeExceptionImpact({ form, editingId, periods }) {
  if (!form.parent_id || !form.start_date || !form.end_date) return null;
  if (form.type !== 'exception_add' && form.type !== 'exception_remove') return null;

  const newStart = parseLocalDate(form.start_date);
  const newEnd = parseLocalDate(form.end_date);
  if (!newStart || !newEnd || newStart > newEnd) return null;

  const list = periods || [];
  const parent = list.find((p) => p.period_id === form.parent_id);
  if (!parent) return null;

  const proposedSortOrder = form.sort_order === '' ? 0 : Number(form.sort_order || 0);
  const proposedMeetingDays = Array.isArray(form.meeting_days) ? form.meeting_days : [];
  const proposedScope = form.scope || 'all';
  const proposedException = {
    period_id: editingId || '__proposed__',
    parent_id: form.parent_id,
    name: form.name || (editingId ? '(this exception)' : '(proposed exception)'),
    type: form.type,
    start_date: form.start_date,
    end_date: form.end_date,
    meeting_days: proposedMeetingDays,
    sort_order: proposedSortOrder,
    scope: proposedScope
  };

  // periodsAfter: substitute editing row with proposed values, or append if new.
  const periodsAfter = editingId
    ? list.map((p) => (p.period_id === editingId ? proposedException : p))
    : [...list, proposedException];

  // Find conflicting descendant exceptions (DEEPER in the tree than form.parent_id)
  const descendantsOfParent = getDescendants(form.parent_id, list);
  const overlapping = [];
  const newMD = proposedMeetingDays.length > 0 ? proposedMeetingDays : null;
  for (const d of descendantsOfParent) {
    if (d.type !== 'exception_add' && d.type !== 'exception_remove') continue;
    if (d.period_id === editingId) continue;
    const dStart = parseLocalDate(d.start_date);
    const dEnd = parseLocalDate(d.end_date);
    if (!dStart || !dEnd) continue;
    if (dEnd < newStart || dStart > newEnd) continue;
    const dMD = Array.isArray(d.meeting_days) && d.meeting_days.length > 0 ? d.meeting_days : null;
    if (newMD && dMD && !newMD.some((x) => dMD.includes(x))) continue;
    // Scopes that can never collide aren't real conflicts (e.g. a student-
    // only break vs. a staff-only break apply to different audiences).
    const dScope = d.scope || 'all';
    const scopesDisjoint =
      proposedScope !== 'all' && dScope !== 'all' && proposedScope !== dScope;
    if (scopesDisjoint) continue;

    // Determine relative depth between d's parent (where d lives) and form.parent_id.
    // We'll use the day-state perspective of d's parent period:
    //   - d itself is depth 0 (it's d's parent's own child)
    //   - the proposed exception, attached at form.parent_id, is at some depth ≥ 1
    let levels = 0;
    let cursorId = d.parent_id;
    while (cursorId && cursorId !== form.parent_id) {
      levels += 1;
      const cursor = list.find((p) => p.period_id === cursorId);
      if (!cursor) {
        levels = -1;
        break;
      }
      cursorId = cursor.parent_id;
    }
    const proposedDepthFromDParent = cursorId === form.parent_id ? levels + 1 : null;
    if (proposedDepthFromDParent === null) continue;

    const dPriority = (10 - 0) * 100 + (d.sort_order || 0);
    const proposedPriority = (10 - proposedDepthFromDParent) * 100 + proposedSortOrder;
    const proposedWins = proposedPriority > dPriority;
    const requiredBoost = proposedWins
      ? 0
      : (d.sort_order || 0) - proposedSortOrder + proposedDepthFromDParent * 100 + 1;

    overlapping.push({
      id: d.period_id,
      name: d.name,
      type: d.type,
      parentName:
        list.find((p) => p.period_id === d.parent_id)?.name || '(unknown)',
      sortOrder: d.sort_order || 0,
      scope: dScope,
      proposedWins,
      requiredBoost
    });
  }

  // Compute net session-day change across schedulable descendants of form.parent_id
  // (and the parent itself if it's schedulable). Track per-period gained/lost so
  // the UI can name the affected children.
  const schedulable = [parent, ...descendantsOfParent].filter(
    (p) => p.type === 'enrollment' || p.type === 'track' || p.type === 'learning'
  );

  let gained = 0;
  let lost = 0;
  const affectedPeriods = [];
  for (const sp of schedulable) {
    const spStart = parseLocalDate(sp.start_date);
    const spEnd = parseLocalDate(sp.end_date);
    if (!spStart || !spEnd) continue;
    const oStart = newStart > spStart ? newStart : spStart;
    const oEnd = newEnd < spEnd ? newEnd : spEnd;
    if (oStart > oEnd) continue;

    const md =
      sp.meeting_days && sp.meeting_days.length > 0
        ? sp.meeting_days
        : [1, 2, 3, 4, 5, 6, 7];

    const exsBefore = effectiveExceptionsFor(sp, list);
    const exsAfter = effectiveExceptionsFor(sp, periodsAfter);

    // Use student-perspective math so the gained/lost stat matches what the
    // user sees in the schedule preview (which is also student-perspective).
    // If they're editing a staff-only exception, this will correctly show
    // "0 session days affected" — exactly the point of role-scoping.
    let pGained = 0;
    let pLost = 0;
    const cur = new Date(oStart);
    while (cur <= oEnd) {
      const before = getDayState(cur, md, exsBefore, 'students');
      const after = getDayState(cur, md, exsAfter, 'students');
      const beforeIs = before === 'session' || before === 'added';
      const afterIs = after === 'session' || after === 'added';
      if (!beforeIs && afterIs) pGained += 1;
      else if (beforeIs && !afterIs) pLost += 1;
      cur.setDate(cur.getDate() + 1);
    }
    gained += pGained;
    lost += pLost;
    if (pGained > 0 || pLost > 0) {
      affectedPeriods.push({
        id: sp.period_id,
        name: sp.name,
        type: sp.type,
        gained: pGained,
        lost: pLost,
        isParent: sp.period_id === form.parent_id
      });
    }
  }

  // Sort affected periods: parent first, then alphabetically by start date / name
  affectedPeriods.sort((a, b) => {
    if (a.isParent !== b.isParent) return a.isParent ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const requiredPriorityToWin = overlapping.reduce(
    (max, o) => (o.proposedWins ? max : Math.max(max, proposedSortOrder + o.requiredBoost)),
    proposedSortOrder
  );

  return {
    overlapping,
    gainedSessionDays: gained,
    lostSessionDays: lost,
    affectedPeriods,
    proposedSortOrder,
    requiredPriorityToWin
  };
}

/** All descendants of a period (flat list, BFS). */
function getDescendants(periodId, allPeriods) {
  const result = [];
  const queue = [periodId];
  const seen = new Set([periodId]);
  while (queue.length) {
    const cur = queue.shift();
    for (const p of allPeriods || []) {
      if (p.parent_id === cur && !seen.has(p.period_id)) {
        seen.add(p.period_id);
        result.push(p);
        queue.push(p.period_id);
      }
    }
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component

const ScheduleBuilder = ({ userData }) => {
  const token = useMemo(() => localStorage.getItem('token'), []);
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const card = useMemo(() => defaultCard(userData), [userData]);
  const globalScope = useMemo(() => isGlobalScope(card), [card]);

  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    type: 'enrollment',
    name: '',
    start_date: '',
    end_date: '',
    parent_id: '',
    subtype: '',
    sort_order: '',
    notes: '',
    meeting_days: [1, 2, 3, 4, 5, 6, 7],
    scope: 'all'
  });
  const [saving, setSaving] = useState(false);

  // Standalone calendar preview (opened from the calendar icon on a card —
  // bypasses the edit modal so the user can inspect a period's schedule
  // without opening the full edit form).
  const [previewPeriod, setPreviewPeriod] = useState(null);
  const previewForm = useMemo(() => {
    if (!previewPeriod) return null;
    return {
      type: previewPeriod.type,
      name: previewPeriod.name || '',
      start_date: previewPeriod.start_date ? String(previewPeriod.start_date).slice(0, 10) : '',
      end_date: previewPeriod.end_date ? String(previewPeriod.end_date).slice(0, 10) : '',
      parent_id: previewPeriod.parent_id || '',
      subtype: previewPeriod.subtype || '',
      sort_order: previewPeriod.sort_order != null ? String(previewPeriod.sort_order) : '',
      notes: previewPeriod.notes || '',
      meeting_days:
        Array.isArray(previewPeriod.meeting_days) && previewPeriod.meeting_days.length
          ? [...previewPeriod.meeting_days]
          : [],
      scope: previewPeriod.scope || 'all'
    };
  }, [previewPeriod]);

  // Canvas viewport
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const initialFitRef = useRef(false);

  // ───── Data loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get('/api/user-management/schools', authHeaders);
        if (!alive) return;
        const list = res.data.schools || [];
        setSchools(list);
        // Preferred default: "Scale Leadership Academy East" (matched case-
        // insensitively against school_name / title). Falls back to existing
        // selection, the user's primary school card, or the first school.
        const preferredName = 'scale leadership academy east';
        const preferred = list.find((s) => {
          const label = (s.school_name || s.title || '').toString().trim().toLowerCase();
          return label === preferredName;
        });
        setSchoolId((prev) => {
          if (prev) return prev;
          if (preferred) return preferred.school_id;
          if (card?.school_id) return card.school_id;
          if (globalScope && list[0]) return list[0].school_id;
          return null;
        });
      } catch (e) {
        console.error(e);
        if (alive) setLoadErr('Could not load schools.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [authHeaders, card?.school_id, globalScope]);

  const fetchPeriods = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setLoadErr(null);
    try {
      const res = await axios.get('/api/schedule-builder/periods', {
        ...authHeaders,
        params: { school_id: schoolId }
      });
      setPeriods(res.data.periods || []);
    } catch (e) {
      console.error(e);
      setLoadErr(e.response?.data?.error || 'Failed to load periods.');
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, schoolId]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  // ───── Layout ──────────────────────────────────────────────────────────────
  const schoolName = useMemo(() => {
    const s = schools.find((x) => x.school_id === schoolId);
    return s?.school_name || 'School';
  }, [schools, schoolId]);

  const layout = useMemo(() => buildLayout(periods, schoolName), [periods, schoolName]);

  // Center the school root on first render of a layout / school change
  useEffect(() => {
    initialFitRef.current = false;
  }, [schoolId]);

  useEffect(() => {
    if (initialFitRef.current) return;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    if (rect.width === 0) return;
    const targetX = rect.width / 2 - layout.bounds.rootX * scale;
    const targetY = CANVAS_TOP_INSET;
    setPan({ x: targetX, y: targetY });
    initialFitRef.current = true;
    // We intentionally compute once per (school) layout and depend only on layout/canvas readiness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.bounds.rootX, schoolId]);

  // ───── Pan / zoom handlers ─────────────────────────────────────────────────
  const onCanvasMouseDown = (e) => {
    // Only start panning if the mousedown originated on the canvas backdrop,
    // not on a card or button (which set data-no-pan="true").
    if (e.button !== 0) return;
    const target = e.target;
    if (target.closest && target.closest('[data-no-pan="true"]')) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPan: { ...pan }
    };
    setIsPanning(true);
  };

  const onCanvasMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({
      x: dragRef.current.startPan.x + dx,
      y: dragRef.current.startPan.y + dy
    });
  };

  const endDrag = () => {
    dragRef.current = null;
    setIsPanning(false);
  };

  // Full-viewport canvas — lock page scroll while this page is mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onWindowMouseUp = () => endDrag();
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, []);

  // Native non-passive wheel listener so we can block page scroll on zoom
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const handler = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      setScale((prevScale) => {
        const nextScale = clamp(prevScale * factor, 0.35, 2.4);
        setPan((prevPan) => {
          const wx = (mx - prevPan.x) / prevScale;
          const wy = (my - prevPan.y) / prevScale;
          return { x: mx - wx * nextScale, y: my - wy * nextScale };
        });
        return nextScale;
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const zoomBy = (factor) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = rect.width / 2;
    const my = rect.height / 2;
    const nextScale = clamp(scale * factor, 0.35, 2.4);
    const wx = (mx - pan.x) / scale;
    const wy = (my - pan.y) / scale;
    setPan({ x: mx - wx * nextScale, y: my - wy * nextScale });
    setScale(nextScale);
  };

  const fitToCanvas = () => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = (rect.width - 64) / layout.bounds.width;
    const sy = (rect.height - CANVAS_TOP_INSET - 64) / layout.bounds.height;
    const next = clamp(Math.min(sx, sy), 0.35, 2.4);
    setScale(next);
    setPan({
      x: rect.width / 2 - (layout.bounds.width / 2) * next,
      y: CANVAS_TOP_INSET
    });
  };

  const resetView = () => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setScale(1);
    setPan({
      x: rect.width / 2 - layout.bounds.rootX,
      y: CANVAS_TOP_INSET
    });
  };

  // ───── CRUD ────────────────────────────────────────────────────────────────
  const openCreate = (type, suggestedParentId) => {
    setEditing(null);
    setForm({
      type,
      name: '',
      start_date: '',
      end_date: '',
      parent_id: suggestedParentId || '',
      subtype: '',
      sort_order: '',
      notes: '',
      meeting_days: type === 'track' ? [1, 2, 3, 4, 5, 6, 7] : [],
      scope: 'all'
    });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      type: p.type,
      name: p.name || '',
      start_date: p.start_date ? String(p.start_date).slice(0, 10) : '',
      end_date: p.end_date ? String(p.end_date).slice(0, 10) : '',
      parent_id: p.parent_id || '',
      subtype: p.subtype || '',
      sort_order: p.sort_order != null ? String(p.sort_order) : '',
      notes: p.notes || '',
      meeting_days: Array.isArray(p.meeting_days) && p.meeting_days.length ? [...p.meeting_days] : [],
      scope: p.scope || 'all'
    });
    setModalOpen(true);
  };

  const toggleWeekday = (n) => {
    setForm((f) => {
      const has = f.meeting_days.includes(n);
      const next = has
        ? f.meeting_days.filter((x) => x !== n)
        : [...f.meeting_days, n].sort((a, b) => a - b);
      return { ...f, meeting_days: next };
    });
  };

  const setMeetingDays = (days) => {
    setForm((f) => ({ ...f, meeting_days: [...days].sort((a, b) => a - b) }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!schoolId) return;
    setSaving(true);
    try {
      const payload = {
        school_id: schoolId,
        type: form.type,
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        subtype: form.subtype || null,
        notes: form.notes || null,
        sort_order: form.sort_order === '' ? null : Number(form.sort_order)
      };
      if (form.type !== 'enrollment') {
        payload.parent_id = form.parent_id || null;
      }
      if (form.type === 'track') {
        payload.meeting_days = form.meeting_days.length ? form.meeting_days : [1, 2, 3, 4, 5, 6, 7];
      } else if (form.type === 'learning' || form.type.startsWith('exception')) {
        payload.meeting_days = form.meeting_days.length ? form.meeting_days : null;
      }
      // Scope only travels for exception types; the API force-resets others
      // to 'all' regardless, but keeping the payload tight is cleaner.
      if (form.type.startsWith('exception')) {
        payload.scope = form.scope || 'all';
      }

      if (editing) {
        await axios.patch(`/api/schedule-builder/periods/${editing.period_id}`, payload, authHeaders);
      } else {
        await axios.post('/api/schedule-builder/periods', payload, authHeaders);
      }
      setModalOpen(false);
      await fetchPeriods();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete “${p.name}”? This cannot be undone if nothing references it.`)) return;
    try {
      await axios.delete(`/api/schedule-builder/periods/${p.period_id}`, {
        ...authHeaders,
        params: { school_id: schoolId }
      });
      await fetchPeriods();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  // ───── Eligible parent options for the modal ───────────────────────────────
  const enrollmentOptions = useMemo(
    () => periods.filter((p) => p.type === 'enrollment'),
    [periods]
  );
  const trackOptions = useMemo(() => periods.filter((p) => p.type === 'track'), [periods]);
  const parentOptionsForLearning = useMemo(
    () => [...enrollmentOptions, ...trackOptions],
    [enrollmentOptions, trackOptions]
  );
  const parentOptionsForException = useMemo(() => {
    const learning = periods.filter((p) => p.type === 'learning');
    return [...enrollmentOptions, ...trackOptions, ...learning];
  }, [periods, enrollmentOptions, trackOptions]);

  // ───── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={canvasRef}
        style={{
          ...styles.canvas,
          cursor: isPanning ? PAN_GRABBING_CURSOR : PAN_GRAB_CURSOR
        }}
        onMouseDown={onCanvasMouseDown}
        onMouseMove={onCanvasMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <div style={styles.canvasToolbar} data-no-pan="true">
          <label style={styles.schoolPicker}>
            <span style={styles.pickerLabel}>School</span>
            <select
              value={schoolId || ''}
              onChange={(e) => setSchoolId(e.target.value)}
              disabled={!globalScope}
              style={styles.select}
            >
              <option value="">Select school…</option>
              {schools.map((s) => (
                <option key={s.school_id} value={s.school_id}>
                  {s.school_name}
                </option>
              ))}
            </select>
          </label>
          {loadErr && <div style={styles.errorInline}>{loadErr}</div>}
        </div>

        {loading && (
          <div style={styles.canvasOverlay}>
            <LoadingSpinner />
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            width: layout.bounds.width,
            height: layout.bounds.height
          }}
        >
          <svg
            width={layout.bounds.width}
            height={layout.bounds.height}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
          >
            {layout.edges.map((edge, i) => {
              const fromX = edge.from.x;
              const fromY = edge.from.y + NODE_H;
              const toX = edge.to.x;
              const toY = edge.to.y;
              const midY = (fromY + toY) / 2;
              const d = `M ${fromX} ${fromY} C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${toY}`;
              const stroke = TYPE_COLOR[edge.type] || '#cbd5e1';
              return (
                <path
                  key={i}
                  d={d}
                  stroke={stroke}
                  strokeOpacity={0.35}
                  strokeWidth={1.2}
                  strokeDasharray="4 4"
                  fill="none"
                />
              );
            })}
          </svg>

          {layout.nodes.map((node) => (
            <NodeCard
              key={node.id}
              node={node}
              hovered={hoverId === node.id}
              onHover={setHoverId}
              onAddChild={(type) => openCreate(type, node.id === SCHOOL_ID ? null : node.id)}
              onEdit={() => node.period && openEdit(node.period)}
              onDelete={() => node.period && handleDelete(node.period)}
              onPreview={() => node.period && setPreviewPeriod(node.period)}
            />
          ))}

          {layout.nodes.length === 1 && !loading && schoolId && (
            <div
              style={{
                position: 'absolute',
                left: layout.bounds.rootX - 140,
                top: layout.bounds.rootY + NODE_H + 28,
                width: 280,
                textAlign: 'center',
                fontSize: '0.78rem',
                color: '#94a3b8'
              }}
            >
              Hover the school and click <span style={styles.kbd}>+ Enrollment year</span> to start
              building this year's schedule.
            </div>
          )}
        </div>

        <div style={styles.zoomControls} data-no-pan="true">
          <button type="button" style={styles.zoomBtn} onClick={() => zoomBy(1 / 1.2)} title="Zoom out">
            −
          </button>
          <button type="button" style={styles.zoomBtnLabel} onClick={resetView} title="Reset view">
            {Math.round(scale * 100)}%
          </button>
          <button type="button" style={styles.zoomBtn} onClick={() => zoomBy(1.2)} title="Zoom in">
            +
          </button>
          <button type="button" style={styles.zoomBtnText} onClick={fitToCanvas} title="Fit">
            Fit
          </button>
        </div>

        <div style={styles.legend} data-no-pan="true">
          {Object.entries(TYPE_COLOR)
            .filter(([k]) => k !== 'school')
            .map(([k, color]) => (
              <span key={k} style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: color }} />
                {SHORT_TYPE_LABEL[k] || k}
              </span>
            ))}
        </div>
      </div>

      {modalOpen && (
        <PeriodModal
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={() => !saving && setModalOpen(false)}
          onSubmit={submitForm}
          enrollmentOptions={enrollmentOptions}
          parentOptionsForLearning={parentOptionsForLearning}
          parentOptionsForException={parentOptionsForException}
          toggleWeekday={toggleWeekday}
          setMeetingDays={setMeetingDays}
          periods={periods}
        />
      )}

      {previewPeriod && previewForm && (
        <SchedulePreviewModal
          editing={previewPeriod}
          form={previewForm}
          periods={periods}
          onClose={() => setPreviewPeriod(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Scope badge — small "person with slash" icon shown on exception cards when
// the exception is restricted to a single role. Hidden for the default 'all'
// scope so existing data has zero visual noise. The slash conveys "this day
// is off — for this role only", which matches how users described it.

const ScopeBadge = ({ scope, size = 14, tooltip }) => {
  if (!scope || scope === 'all') return null;
  const color = SCOPE_COLOR[scope] || '#64748b';
  const label = SCOPE_LABEL[scope] || scope;
  return (
    <span
      title={tooltip || label}
      aria-label={tooltip || label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size + 4,
        height: size + 4,
        borderRadius: 999,
        background: `${color}15`,
        border: `1px solid ${color}55`,
        color,
        flexShrink: 0
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* simple person silhouette (head + shoulders) */}
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
        {/* diagonal strike-through — signals "day off for this role" */}
        <line x1="4" y1="20" x2="20" y2="4" stroke={color} strokeWidth="2.4" />
      </svg>
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Node card

const NodeCard = ({ node, hovered, onHover, onAddChild, onEdit, onDelete, onPreview }) => {
  const accent = TYPE_COLOR[node.type] || '#475569';
  const isSchool = node.type === 'school';
  const allowed = ALLOWED_CHILDREN[node.type] || [];
  const dateLine = node.period ? formatDateRange(node.period) : '';
  const yearLine = node.period ? formatYear(node.period) : '';
  const canPreview =
    !isSchool &&
    node.period &&
    (node.type === 'enrollment' || node.type === 'track' || node.type === 'learning') &&
    node.period.start_date &&
    node.period.end_date;
  const meetingDays =
    node.period?.meeting_days && node.period.meeting_days.length > 0
      ? node.period.meeting_days
          .map((d) => WEEKDAYS.find((w) => w.n === d)?.label || d)
          .join(' · ')
      : '';

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuHoverIdx, setMenuHoverIdx] = useState(-1);
  const menuWrapRef = useRef(null);

  // Close add menu on outside click / escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (!menuWrapRef.current) return;
      if (!menuWrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Close menu if hover ends without an outside click (e.g. user moves to another card)
  useEffect(() => {
    if (!hovered && menuOpen) {
      // Slight delay so a click on a menu item that originated outside the card area still registers
      const t = setTimeout(() => setMenuOpen(false), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [hovered, menuOpen]);

  return (
    <div
      data-no-pan="true"
      style={{
        position: 'absolute',
        left: node.x - NODE_W / 2,
        top: node.y,
        width: NODE_W,
        height: NODE_H,
        zIndex: menuOpen ? 20 : hovered ? 5 : 1
      }}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover((h) => (h === node.id ? null : h))}
    >
      <div
        style={{
          ...styles.card,
          borderLeftColor: accent,
          background: isSchool ? '#0f172a' : '#ffffff',
          color: isSchool ? '#ffffff' : '#0f172a',
          boxShadow: hovered
            ? '0 6px 18px rgba(15, 23, 42, 0.08)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)',
          cursor: isSchool ? 'default' : 'pointer'
        }}
        onClick={isSchool ? undefined : onEdit}
        title={isSchool ? undefined : 'Click to edit'}
      >
        <div style={styles.cardTop}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span
              style={{
                ...styles.typePill,
                color: isSchool ? '#cbd5e1' : accent,
                background: isSchool ? 'rgba(255,255,255,0.06)' : `${accent}15`
              }}
            >
              {isSchool ? 'School' : SHORT_TYPE_LABEL[node.type] || node.type}
            </span>
            {node.period?.scope && node.period.scope !== 'all' && (
              <ScopeBadge
                scope={node.period.scope}
                size={11}
                tooltip={`${SCOPE_LABEL[node.period.scope]} — does not affect ${
                  node.period.scope === 'students' ? 'staff/instructor' : 'student'
                } schedules`}
              />
            )}
          </span>
          {!isSchool && (
            <span
              style={{
                ...styles.cardActions,
                opacity: hovered ? 1 : 0,
                pointerEvents: hovered ? 'auto' : 'none'
              }}
            >
              {canPreview && (
                <button
                  type="button"
                  style={styles.previewIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview && onPreview();
                  }}
                  title="Preview schedule"
                  aria-label="Preview schedule"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                style={styles.deleteX}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                title="Double-click to delete"
                aria-label="Delete (double-click)"
              >
                ×
              </button>
            </span>
          )}
          {isSchool && yearLine && (
            <span style={{ ...styles.year, color: '#cbd5e1' }}>{yearLine}</span>
          )}
        </div>
        <div
          style={{
            ...styles.cardName,
            color: isSchool ? '#ffffff' : '#0f172a'
          }}
          title={node.name}
        >
          {node.name || '(unnamed)'}
        </div>
        {dateLine && (
          <div
            style={{
              ...styles.cardMeta,
              color: isSchool ? '#94a3b8' : '#64748b'
            }}
          >
            {!isSchool && yearLine ? `${yearLine}  ·  ` : ''}
            {dateLine}
            {meetingDays ? `  ·  ${meetingDays}` : ''}
          </div>
        )}
      </div>

      {allowed.length > 0 && (
        <div
          ref={menuWrapRef}
          style={{
            ...styles.addPlusWrap,
            opacity: hovered || menuOpen ? 1 : 0,
            pointerEvents: hovered || menuOpen ? 'auto' : 'none'
          }}
          data-no-pan="true"
        >
          <button
            type="button"
            style={{
              ...styles.addPlus,
              borderColor: accent + '66',
              color: accent
            }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            title="Add child"
            aria-label="Add child"
          >
            +
          </button>
          {menuOpen && (
            <div style={styles.addMenu} onMouseLeave={() => setMenuHoverIdx(-1)}>
              <div style={styles.addMenuHead}>
                Add under {isSchool ? 'school' : truncate(node.name, 18)}
              </div>
              {allowed.map((t, idx) => {
                const isItemHover = menuHoverIdx === idx;
                const itemColor = TYPE_COLOR[t];
                return (
                  <button
                    key={t}
                    type="button"
                    style={{
                      ...styles.addMenuItem,
                      background: isItemHover ? `${itemColor}12` : 'transparent',
                      borderColor: isItemHover ? `${itemColor}40` : 'transparent',
                      color: isItemHover ? itemColor : '#0f172a'
                    }}
                    onMouseEnter={() => setMenuHoverIdx(idx)}
                    onFocus={() => setMenuHoverIdx(idx)}
                    onClick={() => {
                      setMenuOpen(false);
                      setMenuHoverIdx(-1);
                      onAddChild(t);
                    }}
                  >
                    <span
                      style={{
                        ...styles.addMenuIcon,
                        background: isItemHover ? itemColor : `${itemColor}1a`,
                        color: isItemHover ? '#ffffff' : itemColor,
                        borderColor: isItemHover ? itemColor : `${itemColor}55`
                      }}
                      aria-hidden="true"
                    >
                      +
                    </span>
                    <span style={styles.addMenuLabel}>{TYPE_LABEL[t]}</span>
                    <span
                      style={{
                        ...styles.addMenuChevron,
                        opacity: isItemHover ? 1 : 0,
                        transform: isItemHover ? 'translateX(0)' : 'translateX(-4px)',
                        color: itemColor
                      }}
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Exception conflict warning (renders inside the form modal)

const ExceptionConflictWarning = ({ form, editingId, periods }) => {
  const impact = useMemo(
    () => analyzeExceptionImpact({ form, editingId, periods }),
    [
      form.parent_id,
      form.start_date,
      form.end_date,
      form.meeting_days,
      form.sort_order,
      form.type,
      form.scope,
      editingId,
      periods
    ]
  );

  if (!impact) return null;
  const hasOverlaps = impact.overlapping.length > 0;
  const hasNetChange = impact.gainedSessionDays > 0 || impact.lostSessionDays > 0;
  if (!hasOverlaps && !hasNetChange) return null;

  const losing = impact.overlapping.filter((o) => !o.proposedWins);
  const winning = impact.overlapping.filter((o) => o.proposedWins);

  return (
    <div style={styles.conflictPanel}>
      <div style={styles.conflictHeader}>
        <span style={styles.conflictBadge}>Conflict check</span>
        <span style={styles.conflictHeadline}>
          {hasOverlaps
            ? `${impact.overlapping.length} overlapping exception${
                impact.overlapping.length === 1 ? '' : 's'
              }`
            : 'Schedule impact'}
        </span>
      </div>

      <div style={styles.conflictDiffRow}>
        <span style={{ ...styles.conflictDiffStat, color: '#16a34a' }}>
          +{impact.gainedSessionDays} session day{impact.gainedSessionDays === 1 ? '' : 's'}
        </span>
        <span style={{ ...styles.conflictDiffStat, color: '#dc2626' }}>
          −{impact.lostSessionDays} session day{impact.lostSessionDays === 1 ? '' : 's'}
        </span>
        <span style={styles.conflictDiffHint}>
          {hasNetChange
            ? `across ${impact.affectedPeriods.length} affected period${
                impact.affectedPeriods.length === 1 ? '' : 's'
              }`
            : 'no net change at current priority'}
        </span>
      </div>

      {impact.affectedPeriods.length > 0 && (
        <div style={styles.conflictList}>
          <div style={styles.conflictListLabel}>Affected periods:</div>
          {impact.affectedPeriods.map((p) => (
            <div key={p.id} style={styles.conflictItem}>
              <span style={{ ...styles.conflictItemDot, background: TYPE_COLOR[p.type] }} />
              <span style={styles.conflictItemName}>
                {p.name || '(unnamed)'}
                {p.isParent && (
                  <span style={styles.conflictItemBadge}>parent</span>
                )}
              </span>
              <span style={styles.conflictItemMeta}>
                {TYPE_LABEL[p.type]}
                {p.gained > 0 && (
                  <span style={{ color: '#16a34a', marginLeft: 8 }}>+{p.gained}</span>
                )}
                {p.lost > 0 && (
                  <span style={{ color: '#dc2626', marginLeft: 8 }}>−{p.lost}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {losing.length > 0 && (
        <div style={styles.conflictList}>
          <div style={styles.conflictListLabel}>
            Will lose to (more specific exceptions take precedence):
          </div>
          {losing.map((o) => (
            <div key={o.id} style={styles.conflictItem}>
              <span style={{ ...styles.conflictItemDot, background: TYPE_COLOR[o.type] }} />
              <span style={styles.conflictItemName}>
                {o.name || '(unnamed)'}
                {o.scope && o.scope !== 'all' && (
                  <ScopeBadge scope={o.scope} size={10} tooltip={SCOPE_LABEL[o.scope]} />
                )}
              </span>
              <span style={styles.conflictItemMeta}>
                under {o.parentName} · {TYPE_LABEL[o.type]} · priority {o.sortOrder}
                {o.scope && o.scope !== 'all' && ` · ${SCOPE_LABEL[o.scope]}`}
              </span>
            </div>
          ))}
          <div style={styles.conflictHint}>
            To override these, raise this priority to{' '}
            <strong>{impact.requiredPriorityToWin}</strong> or higher.
          </div>
        </div>
      )}

      {winning.length > 0 && (
        <div style={styles.conflictList}>
          <div style={styles.conflictListLabel}>
            Will override (this exception currently wins):
          </div>
          {winning.map((o) => (
            <div key={o.id} style={styles.conflictItem}>
              <span style={{ ...styles.conflictItemDot, background: TYPE_COLOR[o.type] }} />
              <span style={styles.conflictItemName}>
                {o.name || '(unnamed)'}
                {o.scope && o.scope !== 'all' && (
                  <ScopeBadge scope={o.scope} size={10} tooltip={SCOPE_LABEL[o.scope]} />
                )}
              </span>
              <span style={styles.conflictItemMeta}>
                under {o.parentName} · {TYPE_LABEL[o.type]} · priority {o.sortOrder}
                {o.scope && o.scope !== 'all' && ` · ${SCOPE_LABEL[o.scope]}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal

const PeriodModal = ({
  editing,
  form,
  setForm,
  saving,
  onClose,
  onSubmit,
  enrollmentOptions,
  parentOptionsForLearning,
  parentOptionsForException,
  toggleWeekday,
  setMeetingDays,
  periods
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const canPreview =
    (form.type === 'track' || form.type === 'learning' || form.type === 'enrollment') &&
    form.start_date &&
    form.end_date;

  const isExceptionType =
    form.type === 'exception_remove' || form.type === 'exception_add';

  // "Single day" exceptions force end_date == start_date and disable the End input.
  // Default ON for new exceptions; for an existing exception, derive from the row.
  const [isSingleDay, setIsSingleDay] = useState(() => {
    if (!isExceptionType) return false;
    if (!editing) return true;
    const s = editing.start_date ? String(editing.start_date).slice(0, 10) : '';
    const e = editing.end_date ? String(editing.end_date).slice(0, 10) : '';
    return Boolean(s && e && s === e);
  });

  // When the user switches to an exception type on a fresh form, default to single-day.
  useEffect(() => {
    if (isExceptionType && !editing) setIsSingleDay(true);
    if (!isExceptionType) setIsSingleDay(false);
    // We intentionally only react to type / editing changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type, editing]);

  // Keep end_date pinned to start_date while in single-day mode.
  useEffect(() => {
    if (isExceptionType && isSingleDay && form.start_date && form.end_date !== form.start_date) {
      setForm((f) => ({ ...f, end_date: f.start_date }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExceptionType, isSingleDay, form.start_date]);

  const setSingleDay = (v) => {
    setIsSingleDay(v);
    if (v && form.start_date) {
      setForm((f) => ({ ...f, end_date: f.start_date }));
    }
  };

  return (
  <div style={styles.modalOverlay} onClick={onClose} role="presentation">
    <form style={styles.modalCard} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
      <div style={styles.modalHeader}>
        <div style={styles.modalTitle}>
          {editing ? `Edit ${TYPE_LABEL[form.type] || 'period'}` : `New ${TYPE_LABEL[form.type] || 'period'}`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {(form.type === 'track' ||
            form.type === 'learning' ||
            form.type === 'enrollment') && (
            <button
              type="button"
              style={{
                ...styles.previewBtn,
                opacity: canPreview ? 1 : 0.5,
                cursor: canPreview ? 'pointer' : 'not-allowed'
              }}
              onClick={() => canPreview && setPreviewOpen(true)}
              disabled={!canPreview}
              title={
                canPreview
                  ? 'Preview which days fall in session'
                  : 'Set start and end dates to enable preview'
              }
            >
              <span style={styles.previewBtnIcon} aria-hidden="true">
                📅
              </span>
              Preview
            </button>
          )}
          <button type="button" style={styles.ghostButton} onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>
      </div>

      <div style={styles.modalBody}>
        {!editing && (
          <label style={labelStyle}>
            Type
            <select
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  type: e.target.value,
                  meeting_days: e.target.value === 'track' ? [1, 2, 3, 4, 5, 6, 7] : []
                }))
              }
              style={inputStyle}
            >
              <option value="enrollment">Enrollment year</option>
              <option value="track">Track</option>
              <option value="learning">Learning period</option>
              <option value="exception_remove">Exception — remove days</option>
              <option value="exception_add">Exception — add days</option>
            </select>
          </label>
        )}

        <label style={labelStyle}>
          Name
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            style={inputStyle}
          />
        </label>

        {isExceptionType && (
          <label style={labelStyle}>
            Affects{' '}
            <span style={{ fontWeight: 400, color: '#94a3b8' }}>
              ("Students only" means staff / instructor SLAs still count this as a working day)
            </span>
            <div style={styles.quickRow}>
              {SCOPE_OPTIONS.map((opt) => {
                const active = (form.scope || 'all') === opt.id;
                const color = SCOPE_COLOR[opt.id];
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, scope: opt.id }))}
                    title={opt.label}
                    style={{
                      ...styles.quickChip,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      ...(active
                        ? {
                            ...styles.quickChipActive,
                            borderColor: color,
                            background: `${color}18`,
                            color
                          }
                        : {})
                    }}
                  >
                    {opt.id !== 'all' && <ScopeBadge scope={opt.id} size={11} tooltip={opt.label} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </label>
        )}

        {isExceptionType && (
          <div style={styles.radioRow} role="radiogroup" aria-label="Exception duration">
            <label style={{ ...styles.radioOption, ...(isSingleDay ? styles.radioOptionActive : {}) }}>
              <input
                type="radio"
                name="exception-duration"
                checked={isSingleDay}
                onChange={() => setSingleDay(true)}
              />
              Single day
            </label>
            <label style={{ ...styles.radioOption, ...(!isSingleDay ? styles.radioOptionActive : {}) }}>
              <input
                type="radio"
                name="exception-duration"
                checked={!isSingleDay}
                onChange={() => setSingleDay(false)}
              />
              Date range
            </label>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={labelStyle}>
            {isExceptionType && isSingleDay ? 'Date' : 'Start'}
            <input
              type="date"
              required
              value={form.start_date}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  start_date: v,
                  end_date: isExceptionType && isSingleDay ? v : f.end_date
                }));
              }}
              style={inputStyle}
            />
          </label>
          <label
            style={{
              ...labelStyle,
              opacity: isExceptionType && isSingleDay ? 0.55 : 1
            }}
          >
            End
            <input
              type="date"
              required
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              disabled={isExceptionType && isSingleDay}
              style={{
                ...inputStyle,
                background: isExceptionType && isSingleDay ? '#f1f5f9' : '#ffffff',
                cursor: isExceptionType && isSingleDay ? 'not-allowed' : 'auto'
              }}
              title={
                isExceptionType && isSingleDay
                  ? 'End date is locked to the start date for single-day exceptions'
                  : undefined
              }
            />
          </label>
        </div>

        {form.type === 'track' && (
          <label style={labelStyle}>
            Parent (enrollment year)
            <select
              required
              value={form.parent_id}
              onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {enrollmentOptions.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {form.type === 'learning' && (
          <label style={labelStyle}>
            Parent (enrollment year or track)
            <select
              required
              value={form.parent_id}
              onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {parentOptionsForLearning.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  [{TYPE_LABEL[p.type]}] {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {(form.type === 'exception_remove' || form.type === 'exception_add') && (
          <label style={labelStyle}>
            Parent (enrollment, track, or learning period)
            <select
              required
              value={form.parent_id}
              onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {parentOptionsForException.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  [{TYPE_LABEL[p.type]}] {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label style={labelStyle}>
          Subtype{' '}
          <span style={{ fontWeight: 400, color: '#94a3b8' }}>
            (optional · usually used on exceptions to categorize them, e.g. holiday or makeup)
          </span>
          {isExceptionType && (
            <div style={styles.quickRow}>
              {SUBTYPE_PRESETS_EXCEPTION.map((s) => {
                const active = form.subtype === s;
                return (
                  <button
                    key={s}
                    type="button"
                    style={{
                      ...styles.quickChip,
                      ...(active ? styles.quickChipActive : {})
                    }}
                    onClick={() =>
                      setForm((f) => ({ ...f, subtype: active ? '' : s }))
                    }
                    title={`Set subtype to ${s}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}
          <input
            value={form.subtype}
            onChange={(e) => setForm((f) => ({ ...f, subtype: e.target.value }))}
            placeholder={
              isExceptionType
                ? 'e.g. holiday, makeup, closure, weather'
                : 'e.g. summer_session'
            }
            style={inputStyle}
          />
        </label>

        {form.type === 'learning' && (
          <label style={labelStyle}>
            Sort order <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              style={inputStyle}
            />
          </label>
        )}

        {isExceptionType && (
          <ExceptionConflictWarning
            form={form}
            editingId={editing?.period_id}
            periods={periods}
          />
        )}

        {isExceptionType && (
          <label style={labelStyle}>
            Priority{' '}
            <span style={{ fontWeight: 400, color: '#94a3b8' }}>
              (optional · default 0 · use 100+ to override parent-level conflicts, 200+ for grandparent-level)
            </span>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              placeholder="0"
              style={inputStyle}
            />
          </label>
        )}

        {(form.type === 'track' ||
          form.type === 'learning' ||
          form.type === 'exception_remove' ||
          form.type === 'exception_add') && (
          <fieldset
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 4 }}
          >
            <legend style={{ fontWeight: 600, fontSize: '0.8rem' }}>Meeting days</legend>
            <p style={{ margin: '0 0 6px', fontSize: 12, color: '#64748b' }}>
              {form.type === 'track' &&
                'Which weekdays this cohort attends. Default: all seven days in range.'}
              {(form.type === 'learning' || form.type.startsWith('exception')) &&
                'Optional filter within the date range. Leave empty to use all calendar days.'}
            </p>
            <MeetingDaysQuickSelect
              meetingDays={form.meeting_days}
              setMeetingDays={setMeetingDays}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {WEEKDAYS.map((w) => (
                <label
                  key={w.n}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 13 }}
                >
                  <input
                    type="checkbox"
                    checked={form.meeting_days.includes(w.n)}
                    onChange={() => toggleWeekday(w.n)}
                  />
                  {w.label}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <label style={labelStyle}>
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
      </div>

      <div style={styles.modalFooter}>
        <button type="button" style={styles.ghostButton} onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="submit" style={styles.primaryButton} disabled={saving}>
          {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
        </button>
      </div>
    </form>
    {previewOpen && (
      <SchedulePreviewModal
        editing={editing}
        form={form}
        periods={periods}
        onClose={() => setPreviewOpen(false)}
      />
    )}
  </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Schedule preview (calendar)

const SchedulePreviewModal = ({ editing, form, periods, onClose }) => {
  const start = parseLocalDate(form.start_date);
  const end = parseLocalDate(form.end_date);
  // Default to the period's own schedule view; users can flip to "Children"
  // when they want to inspect cascaded LP / track schedules.
  const initialChildSections = editing
    ? buildChildSections({ editing, form, periods, trackOverlay: null })
    : [];
  const [view, setView] = useState('schedule');
  const handleBackdrop = (e) => {
    e.stopPropagation();
    onClose();
  };
  if (!start || !end || start > end) {
    return (
      <div style={styles.modalOverlay} onClick={handleBackdrop} role="presentation">
        <div style={styles.previewCard} onClick={(e) => e.stopPropagation()}>
          <div style={styles.previewHeader}>
            <div style={styles.modalTitle}>Schedule preview</div>
            <button type="button" style={styles.ghostButton} onClick={onClose}>
              Close
            </button>
          </div>
          <div style={{ padding: 24, color: '#64748b', fontSize: '0.85rem' }}>
            Pick a valid start and end date, then re-open the preview.
          </div>
        </div>
      </div>
    );
  }

  // Effective meeting days for the period being edited/created.
  // Enrollment has no meeting_days of its own — treat it as "every day in range".
  const effectiveMeetingDays =
    form.meeting_days && form.meeting_days.length > 0
      ? form.meeting_days
      : [1, 2, 3, 4, 5, 6, 7];

  // Exceptions that affect THIS period: own children (depth 0) plus inherited
  // from ancestors (depth 1+). Day-state computation uses depth + sort_order
  // for priority resolution.
  const exceptions = editing ? effectiveExceptionsFor(editing, periods || []) : [];

  // For learning periods: parent track overlay (blue border)
  let trackOverlay = null;
  if (form.type === 'learning' && form.parent_id) {
    const parent = (periods || []).find((p) => p.period_id === form.parent_id);
    if (parent && parent.type === 'track') {
      trackOverlay = {
        name: parent.name,
        start: parseLocalDate(parent.start_date),
        end: parseLocalDate(parent.end_date),
        meeting_days:
          parent.meeting_days && parent.meeting_days.length > 0
            ? parent.meeting_days
            : [1, 2, 3, 4, 5, 6, 7]
      };
    }
  }

  const months = enumerateMonths(start, end);
  const totalDays = Math.round((end - start) / 86400000) + 1;
  let sessionCount = 0;
  let addedCount = 0;
  let removedCount = 0;

  // Pre-compute per-day state. Tracks/LPs are student-attendance calendars,
  // so any staff-only exception is ignored here — it shouldn't pollute the
  // student schedule preview.
  const dayStates = new Map();
  const dayScopes = new Map();
  const trackOverlayDays = new Set();
  const cur = new Date(start);
  while (cur <= end) {
    const key = fmtLocalDate(cur);
    const detail = getDayDetail(cur, effectiveMeetingDays, exceptions, 'students');
    const state = detail.state;
    dayStates.set(key, state);
    if (detail.scope && detail.scope !== 'all') dayScopes.set(key, detail.scope);
    if (state === 'session') sessionCount += 1;
    else if (state === 'added') addedCount += 1;
    else if (state === 'removed') removedCount += 1;

    if (trackOverlay && cur >= trackOverlay.start && cur <= trackOverlay.end) {
      const wd = isoWeekday(cur);
      if (trackOverlay.meeting_days.includes(wd)) trackOverlayDays.add(key);
    }
    cur.setDate(cur.getDate() + 1);
  }

  const titleType = TYPE_LABEL[form.type] || 'Period';
  const periodName = form.name || (editing ? editing.name : 'Untitled');

  // Reuse the sections we computed for the initial-view decision so we don't
  // pay for the work twice.
  const childSections = initialChildSections;

  const hasChildren = childSections.length > 0;
  const effectiveView = hasChildren ? view : 'schedule';

  // Roll-up totals across all child sections (used in the Children tab summary)
  const childrenTotals = childSections.reduce(
    (acc, s) => {
      const totalDays =
        Math.round((s.rangeEnd - s.rangeStart) / 86400000) + 1;
      acc.days += totalDays;
      acc.session += s.counts.session;
      acc.added += s.counts.added;
      acc.removed += s.counts.removed;
      return acc;
    },
    { days: 0, session: 0, added: 0, removed: 0 }
  );

  return (
    <div style={styles.modalOverlay} onClick={handleBackdrop} role="presentation">
      <div style={styles.previewCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.previewHeader}>
          <div>
            <div style={styles.modalTitle}>
              {titleType} preview · {periodName}
            </div>
            <div style={styles.previewSubtitle}>
              {form.start_date} → {form.end_date}
              {trackOverlay ? `  ·  Track overlay: ${trackOverlay.name}` : ''}
              {form.scope && form.scope !== 'all' && (
                <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  ·{' '}
                  <ScopeBadge scope={form.scope} size={10} tooltip={SCOPE_LABEL[form.scope]} />
                  <span style={{ color: SCOPE_COLOR[form.scope], fontWeight: 600 }}>
                    {SCOPE_LABEL[form.scope]}
                  </span>
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {hasChildren && (
              <div style={styles.previewTabs} role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={effectiveView === 'schedule'}
                  style={{
                    ...styles.previewTab,
                    ...(effectiveView === 'schedule' ? styles.previewTabActive : {})
                  }}
                  onClick={() => setView('schedule')}
                >
                  {form.type === 'enrollment'
                    ? 'Year'
                    : form.type === 'track'
                      ? 'Track'
                      : 'Period'}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={effectiveView === 'children'}
                  style={{
                    ...styles.previewTab,
                    ...(effectiveView === 'children' ? styles.previewTabActive : {})
                  }}
                  onClick={() => setView('children')}
                >
                  Children
                  <span style={styles.previewTabBadge}>{childSections.length}</span>
                </button>
              </div>
            )}
            <button type="button" style={styles.ghostButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {effectiveView === 'schedule' && (
          <>
            <div style={styles.previewSummaryRow}>
              <SummaryStat label="Days in range" value={totalDays} color="#475569" />
              <SummaryStat label="Session days" value={sessionCount} color="#16a34a" />
              {addedCount > 0 && <SummaryStat label="Added" value={addedCount} color="#ca8a04" />}
              {removedCount > 0 && (
                <SummaryStat label="Removed" value={removedCount} color="#dc2626" />
              )}
              <SummaryStat
                label="Net session"
                value={sessionCount + addedCount}
                color="#0f172a"
              />
            </div>

            <div style={styles.previewBody}>
              <div style={styles.previewMonthsGrid}>
                {months.map(({ year, month }) => (
                  <MonthGrid
                    key={`${year}-${month}`}
                    year={year}
                    month={month}
                    rangeStart={start}
                    rangeEnd={end}
                    dayStates={dayStates}
                    dayScopes={dayScopes}
                    trackOverlayDays={trackOverlayDays}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {effectiveView === 'children' && (
          <>
            <div style={styles.previewSummaryRow}>
              <SummaryStat
                label={
                  form.type === 'enrollment'
                    ? 'Tracks'
                    : form.type === 'track'
                      ? 'Learning periods'
                      : 'Exceptions'
                }
                value={childSections.length}
                color="#475569"
              />
              <SummaryStat label="Days in range" value={childrenTotals.days} color="#475569" />
              <SummaryStat
                label="Session days"
                value={childrenTotals.session}
                color="#16a34a"
              />
              {childrenTotals.added > 0 && (
                <SummaryStat label="Added" value={childrenTotals.added} color="#ca8a04" />
              )}
              {childrenTotals.removed > 0 && (
                <SummaryStat label="Removed" value={childrenTotals.removed} color="#dc2626" />
              )}
              <SummaryStat
                label="Net session"
                value={childrenTotals.session + childrenTotals.added}
                color="#0f172a"
              />
            </div>
            <div style={styles.previewBody}>
              {childSections.map((section) => (
                <ChildSection key={section.id} section={section} />
              ))}
            </div>
          </>
        )}

        <div style={styles.previewLegend}>
          <LegendChip color={STATE_COLOR.session} label="In session" />
          <LegendChip color={STATE_COLOR.added} label="Added by exception" />
          <LegendChip color={STATE_COLOR.removed} label="Removed by exception" />
          <LegendChip color={STATE_COLOR.off} label="Not in session" />
          {(trackOverlay || (effectiveView === 'children' && form.type === 'track')) && (
            <span style={{ ...styles.legendChip, gap: 6 }}>
              <span style={styles.legendOverlayBox} />
              {form.type === 'track' && effectiveView === 'children'
                ? 'Track session day'
                : 'Parent track day'}
            </span>
          )}
          <span style={{ ...styles.legendChip, gap: 6 }} title="Days affected by a role-scoped exception are marked with a corner letter.">
            <span style={styles.legendScopeLetter} aria-hidden="true">
              <span style={{ color: SCOPE_COLOR.students }}>S</span>
              <span style={{ color: SCOPE_COLOR.staff, marginLeft: 4 }}>T</span>
            </span>
            S = students-only · T = staff-only
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Build the "Children" view sections.
 *
 * - When editing a TRACK: one section per Learning period child. Each section
 *   shows the LP's session/exception coloring with the parent track's session
 *   days outlined in blue.
 * - When editing a LEARNING period: one section per exception child (already
 *   reflected in the default schedule view as yellow/red, but isolated here
 *   for clarity).
 */
function buildChildSections({ editing, form, periods }) {
  if (!editing) return [];
  const list = periods || [];

  const exceptionsFor = (period) => effectiveExceptionsFor(period, list);

  // ── Enrollment → tracks (each section reflects enrollment-level exceptions)
  if (form.type === 'enrollment') {
    const tracks = list
      .filter((p) => p.parent_id === editing.period_id && p.type === 'track')
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
    return tracks
      .map((tr) => {
        const trStart = parseLocalDate(tr.start_date);
        const trEnd = parseLocalDate(tr.end_date);
        if (!trStart || !trEnd || trStart > trEnd) return null;
        return computeSection({
          id: tr.period_id,
          title: tr.name,
          subtitle: `Track · ${formatLpDateString(tr)}`,
          accent: TYPE_COLOR.track,
          rangeStart: trStart,
          rangeEnd: trEnd,
          meetingDays:
            tr.meeting_days && tr.meeting_days.length > 0
              ? tr.meeting_days
              : [1, 2, 3, 4, 5, 6, 7],
          exceptions: exceptionsFor(tr)
        });
      })
      .filter(Boolean);
  }

  // ── Track → learning periods (with cascade up to enrollment)
  if (form.type === 'track') {
    const trackMeetingDays =
      editing.meeting_days && editing.meeting_days.length > 0
        ? editing.meeting_days
        : [1, 2, 3, 4, 5, 6, 7];
    const trackStart = parseLocalDate(editing.start_date);
    const trackEnd = parseLocalDate(editing.end_date);

    const lps = list
      .filter((p) => p.parent_id === editing.period_id && p.type === 'learning')
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
    return lps
      .map((lp) => {
        const lpStart = parseLocalDate(lp.start_date);
        const lpEnd = parseLocalDate(lp.end_date);
        if (!lpStart || !lpEnd || lpStart > lpEnd) return null;
        return computeSection({
          id: lp.period_id,
          title: lp.name,
          subtitle: `Learning period · ${formatLpDateString(lp)}`,
          accent: TYPE_COLOR.learning,
          rangeStart: lpStart,
          rangeEnd: lpEnd,
          meetingDays:
            lp.meeting_days && lp.meeting_days.length > 0
              ? lp.meeting_days
              : [1, 2, 3, 4, 5, 6, 7],
          exceptions: exceptionsFor(lp),
          overlay: {
            start: trackStart,
            end: trackEnd,
            meeting_days: trackMeetingDays
          }
        });
      })
      .filter(Boolean);
  }

  // ── Learning period → its own exception children (each shown isolated)
  if (form.type === 'learning') {
    const exChildren = list.filter(
      (p) =>
        p.parent_id === editing.period_id &&
        (p.type === 'exception_add' || p.type === 'exception_remove')
    );
    return exChildren
      .map((ex) => {
        const exStart = parseLocalDate(ex.start_date);
        const exEnd = parseLocalDate(ex.end_date);
        if (!exStart || !exEnd || exStart > exEnd) return null;
        return computeSection({
          id: ex.period_id,
          title: ex.name,
          subtitle: `${TYPE_LABEL[ex.type]} · ${formatLpDateString(ex)}`,
          accent: TYPE_COLOR[ex.type],
          rangeStart: exStart,
          rangeEnd: exEnd,
          meetingDays:
            ex.meeting_days && ex.meeting_days.length > 0
              ? ex.meeting_days
              : [1, 2, 3, 4, 5, 6, 7],
          exceptions: [],
          overlay: {
            start: parseLocalDate(editing.start_date),
            end: parseLocalDate(editing.end_date),
            meeting_days:
              editing.meeting_days && editing.meeting_days.length > 0
                ? editing.meeting_days
                : [1, 2, 3, 4, 5, 6, 7]
          },
          forceState: ex.type === 'exception_add' ? 'added' : 'removed'
        });
      })
      .filter(Boolean);
  }

  return [];
}

function computeSection({
  id,
  title,
  subtitle,
  accent,
  rangeStart,
  rangeEnd,
  meetingDays,
  exceptions,
  overlay,
  forceState
}) {
  const months = enumerateMonths(rangeStart, rangeEnd);
  const dayStates = new Map();
  const dayScopes = new Map();
  const overlayDays = new Set();
  let session = 0;
  let added = 0;
  let removed = 0;
  const cur = new Date(rangeStart);
  while (cur <= rangeEnd) {
    const key = fmtLocalDate(cur);
    let state;
    if (forceState) {
      state = forceState;
    } else {
      const detail = getDayDetail(cur, meetingDays, exceptions, 'students');
      state = detail.state;
      if (detail.scope && detail.scope !== 'all') dayScopes.set(key, detail.scope);
    }
    dayStates.set(key, state);
    if (state === 'session') session += 1;
    else if (state === 'added') added += 1;
    else if (state === 'removed') removed += 1;
    if (overlay && overlay.start && overlay.end && cur >= overlay.start && cur <= overlay.end) {
      const wd = isoWeekday(cur);
      const dayList =
        overlay.meeting_days && overlay.meeting_days.length > 0
          ? overlay.meeting_days
          : [1, 2, 3, 4, 5, 6, 7];
      if (dayList.includes(wd)) overlayDays.add(key);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return {
    id,
    title,
    subtitle,
    accent,
    rangeStart,
    rangeEnd,
    months,
    dayStates,
    dayScopes,
    overlayDays,
    counts: { session, added, removed }
  };
}

function formatLpDateString(p) {
  if (!p?.start_date) return '';
  const s = String(p.start_date).slice(0, 10);
  const e = p.end_date ? String(p.end_date).slice(0, 10) : '';
  return e && e !== s ? `${s} → ${e}` : s;
}

const ChildSection = ({ section }) => (
  <div style={styles.childSection}>
    <div style={{ ...styles.childSectionHeader, borderLeftColor: section.accent }}>
      <div>
        <div style={styles.childSectionTitle}>{section.title}</div>
        <div style={styles.childSectionSubtitle}>{section.subtitle}</div>
      </div>
      <div style={styles.childSectionStats}>
        {section.counts.session > 0 && (
          <span style={{ ...styles.childCountChip, ...styles.childCountChipSession }}>
            {section.counts.session} session
          </span>
        )}
        {section.counts.added > 0 && (
          <span style={{ ...styles.childCountChip, ...styles.childCountChipAdded }}>
            {section.counts.added} added
          </span>
        )}
        {section.counts.removed > 0 && (
          <span style={{ ...styles.childCountChip, ...styles.childCountChipRemoved }}>
            {section.counts.removed} removed
          </span>
        )}
      </div>
    </div>
    <div style={styles.previewMonthsGrid}>
      {section.months.map(({ year, month }) => (
        <MonthGrid
          key={`${year}-${month}`}
          year={year}
          month={month}
          rangeStart={section.rangeStart}
          rangeEnd={section.rangeEnd}
          dayStates={section.dayStates}
          dayScopes={section.dayScopes}
          trackOverlayDays={section.overlayDays}
        />
      ))}
    </div>
  </div>
);

const SUBTYPE_PRESETS_EXCEPTION = ['holiday', 'makeup', 'closure', 'weather', 'break'];

const MEETING_DAYS_PRESETS = [
  { id: 'all', label: 'Select all', days: [1, 2, 3, 4, 5, 6, 7], match: 'all' },
  { id: 'weekdays', label: 'Mon–Fri', days: [1, 2, 3, 4, 5], match: 'exact' },
  { id: 'weekend', label: 'Sat–Sun', days: [6, 7], match: 'exact' },
  { id: 'none', label: 'Clear', days: [], match: 'none' }
];

function arraysEqualSorted(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}

const MeetingDaysQuickSelect = ({ meetingDays, setMeetingDays }) => (
  <div style={styles.quickRow}>
    {MEETING_DAYS_PRESETS.map((p) => {
      let active;
      if (p.match === 'all') active = meetingDays.length === 7;
      else if (p.match === 'none') active = meetingDays.length === 0;
      else active = arraysEqualSorted(meetingDays, p.days);
      return (
        <button
          key={p.id}
          type="button"
          style={{
            ...styles.quickChip,
            ...(active ? styles.quickChipActive : {})
          }}
          onClick={() => setMeetingDays(p.days)}
          title={p.label}
        >
          {p.label}
        </button>
      );
    })}
  </div>
);

const SummaryStat = ({ label, value, color }) => (
  <div style={styles.previewStat}>
    <div style={{ ...styles.previewStatValue, color }}>{value}</div>
    <div style={styles.previewStatLabel}>{label}</div>
  </div>
);

const LegendChip = ({ color, label }) => (
  <span style={styles.legendChip}>
    <span
      style={{
        ...styles.legendDotSquare,
        background: color.bg,
        border: `1px solid ${color.border}`
      }}
    />
    {label}
  </span>
);

const MonthGrid = ({
  year,
  month,
  rangeStart,
  rangeEnd,
  dayStates,
  dayScopes,
  trackOverlayDays
}) => {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay(); // 0 (Sun) → 6 (Sat)

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push({ blank: true, key: `b${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const inRange = date >= rangeStart && date <= rangeEnd;
    const key = fmtLocalDate(date);
    const state = inRange ? dayStates.get(key) || 'off' : null;
    const overlay = trackOverlayDays.has(key);
    const scope = inRange && dayScopes ? dayScopes.get(key) : null;
    cells.push({ blank: false, key, day: d, state, overlay, inRange, scope });
  }
  while (cells.length % 7 !== 0) cells.push({ blank: true, key: `t${cells.length}` });

  return (
    <div style={styles.monthBox}>
      <div style={styles.monthHeader}>
        {MONTH_NAMES[month]} {year}
      </div>
      <div style={styles.weekdayRow}>
        {WEEKDAY_HEADER.map((w, i) => (
          <div key={i} style={styles.weekdayCell}>
            {w}
          </div>
        ))}
      </div>
      <div style={styles.daysGrid}>
        {cells.map((c) => {
          if (c.blank) return <div key={c.key} style={styles.dayCellBlank} />;
          if (!c.inRange) {
            return (
              <div
                key={c.key}
                style={{
                  ...styles.dayCell,
                  background: '#ffffff',
                  borderColor: '#f1f5f9',
                  color: '#cbd5e1'
                }}
              >
                {c.day}
              </div>
            );
          }
          const palette = STATE_COLOR[c.state] || STATE_COLOR.off;
          const scopeColor = c.scope ? SCOPE_COLOR[c.scope] : null;
          const scopeLetter = c.scope === 'students' ? 'S' : c.scope === 'staff' ? 'T' : null;
          const scopeTitle =
            c.scope === 'students'
              ? ' · students only (staff still working)'
              : c.scope === 'staff'
                ? ' · staff only (students still attending)'
                : '';
          return (
            <div
              key={c.key}
              style={{
                ...styles.dayCell,
                position: 'relative',
                background: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
                outline: c.overlay ? '2px solid #3b82f6' : 'none',
                outlineOffset: c.overlay ? '-2px' : 0
              }}
              title={`${c.key} — ${c.state}${c.overlay ? ' · also a track day' : ''}${scopeTitle}`}
            >
              {c.day}
              {scopeLetter && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 1,
                    right: 2,
                    fontSize: '0.55rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    color: scopeColor,
                    letterSpacing: 0
                  }}
                >
                  {scopeLetter}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles

const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif';

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 12,
  fontWeight: 600,
  fontSize: '0.78rem',
  color: '#334155'
};

const inputStyle = {
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  fontSize: 14,
  fontFamily: fontStack,
  outline: 'none',
  color: '#0f172a',
  background: '#ffffff'
};

const styles = {
  schoolPicker: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
    pointerEvents: 'auto'
  },
  pickerLabel: {
    fontSize: '0.72rem',
    color: '#94a3b8',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  select: {
    padding: '6px 10px',
    fontSize: '0.85rem',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    color: '#0f172a',
    minWidth: 220,
    fontFamily: fontStack
  },
  errorInline: {
    padding: '5px 10px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    borderRadius: 6,
    fontSize: '0.78rem',
    maxWidth: 360,
    lineHeight: 1.35,
    pointerEvents: 'auto'
  },
  canvas: {
    position: 'fixed',
    inset: 0,
    width: '100vw',
    height: '100dvh',
    zIndex: 1100,
    fontFamily: fontStack,
    color: '#0f172a',
    backgroundColor: '#f8fafc',
    backgroundImage:
      'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)',
    backgroundSize: '14px 14px',
    overflow: 'hidden',
    userSelect: 'none'
  },
  canvasToolbar: {
    position: 'absolute',
    top: 12,
    left: 12,
    right: 12,
    zIndex: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    pointerEvents: 'none'
  },
  canvasOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(248, 250, 252, 0.7)',
    zIndex: 5
  },
  card: {
    height: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px 8px 12px',
    border: '1px solid #e2e8f0',
    borderLeft: '3px solid #cbd5e1',
    borderRadius: 8,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    transition: 'box-shadow 0.12s ease'
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6
  },
  typePill: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '2px 6px',
    borderRadius: 999
  },
  year: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#94a3b8'
  },
  cardName: {
    fontSize: '0.86rem',
    fontWeight: 600,
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  cardMeta: {
    fontSize: '0.7rem',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  cardActions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    transition: 'opacity 0.12s ease'
  },
  previewIcon: {
    width: 20,
    height: 20,
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    borderRadius: 999,
    color: '#64748b',
    cursor: 'pointer',
    fontFamily: fontStack,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.12s ease, background 0.12s ease, border-color 0.12s ease'
  },
  deleteX: {
    width: 20,
    height: 20,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    borderRadius: 999,
    color: '#fca5a5',
    fontSize: '0.95rem',
    lineHeight: 1,
    cursor: 'pointer',
    fontFamily: fontStack,
    fontWeight: 600,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.12s ease, background 0.12s ease, border-color 0.12s ease'
  },
  addPlusWrap: {
    position: 'absolute',
    bottom: -14,
    left: '50%',
    transform: 'translateX(-50%)',
    transition: 'opacity 0.12s ease',
    zIndex: 2
  },
  addPlus: {
    width: 28,
    height: 28,
    border: '1.5px solid #cbd5e1',
    background: '#ffffff',
    borderRadius: 999,
    fontSize: '1.05rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#475569',
    fontFamily: fontStack,
    boxShadow: '0 2px 6px rgba(15,23,42,0.08)',
    lineHeight: 1,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  addMenu: {
    position: 'absolute',
    top: 38,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: 6,
    minWidth: 220,
    boxShadow: '0 12px 32px rgba(15,23,42,0.15), 0 1px 2px rgba(15,23,42,0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    zIndex: 10
  },
  addMenuHead: {
    padding: '4px 8px 6px',
    margin: '0 0 2px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.66rem',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  addMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 7,
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#0f172a',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: fontStack,
    transition:
      'background 0.12s ease, border-color 0.12s ease, color 0.12s ease, transform 0.08s ease',
    outline: 'none'
  },
  addMenuIcon: {
    width: 22,
    height: 22,
    borderRadius: 999,
    border: '1px solid #cbd5e1',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
    fontWeight: 700,
    flexShrink: 0,
    transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease'
  },
  addMenuLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  addMenuChevron: {
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1,
    transition: 'opacity 0.12s ease, transform 0.12s ease',
    flexShrink: 0
  },
  zoomControls: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 4,
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)'
  },
  zoomBtn: {
    width: 28,
    height: 28,
    border: 0,
    background: 'transparent',
    fontSize: '1.05rem',
    cursor: 'pointer',
    color: '#475569',
    borderRadius: 6,
    fontFamily: fontStack,
    fontWeight: 600
  },
  zoomBtnLabel: {
    minWidth: 48,
    height: 28,
    border: 0,
    background: 'transparent',
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: '#475569',
    borderRadius: 6,
    fontFamily: fontStack,
    fontWeight: 600
  },
  zoomBtnText: {
    height: 28,
    border: 0,
    background: 'transparent',
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: '#475569',
    borderRadius: 6,
    fontFamily: fontStack,
    fontWeight: 600,
    padding: '0 8px'
  },
  legend: {
    position: 'absolute',
    left: 12,
    bottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '6px 10px',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
    fontSize: '0.7rem',
    color: '#475569',
    flexWrap: 'wrap',
    maxWidth: 'calc(100% - 200px)'
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  legendDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: 999
  },
  kbd: {
    display: 'inline-block',
    padding: '0 5px',
    fontSize: '0.7rem',
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    background: '#ffffff',
    color: '#0f172a',
    fontFamily: fontStack,
    fontWeight: 600
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.32)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '64px 16px 16px'
  },
  modalCard: {
    width: 'min(560px, 100%)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    fontFamily: fontStack
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderBottom: '1px solid #e2e8f0'
  },
  modalTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#0f172a'
  },
  modalBody: {
    padding: 12,
    overflowY: 'auto'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '10px 12px',
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc'
  },
  primaryButton: {
    padding: '7px 14px',
    backgroundColor: '#0f172a',
    color: '#fff',
    border: '1px solid #0f172a',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  ghostButton: {
    padding: '6px 10px',
    backgroundColor: 'transparent',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  previewBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    background: '#f8fafc',
    color: '#0f172a',
    border: '1px solid #cbd5e1',
    borderRadius: 999,
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  conflictPanel: {
    marginBottom: 12,
    padding: 12,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 8
  },
  conflictHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8
  },
  conflictBadge: {
    padding: '2px 7px',
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#9a3412',
    background: '#ffedd5',
    border: '1px solid #fdba74',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  conflictHeadline: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#7c2d12'
  },
  conflictDiffRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    fontSize: '0.78rem',
    fontWeight: 600,
    marginBottom: 8
  },
  conflictDiffStat: {
    fontWeight: 700
  },
  conflictDiffHint: {
    fontSize: '0.7rem',
    color: '#9a3412',
    fontWeight: 500
  },
  conflictList: {
    background: '#ffffff',
    border: '1px solid #fed7aa',
    borderRadius: 6,
    padding: 8,
    marginTop: 6
  },
  conflictListLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#9a3412',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6
  },
  conflictItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
    fontSize: '0.76rem',
    color: '#0f172a'
  },
  conflictItemDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    flexShrink: 0
  },
  conflictItemName: {
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6
  },
  conflictItemBadge: {
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '1px 6px',
    background: '#e0e7ff',
    color: '#4338ca',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: 0.4
  },
  conflictItemMeta: {
    color: '#64748b',
    fontWeight: 400,
    fontSize: '0.7rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  conflictHint: {
    marginTop: 8,
    padding: '6px 8px',
    background: '#fef3c7',
    border: '1px solid #fde68a',
    borderRadius: 6,
    fontSize: '0.72rem',
    color: '#78350f'
  },
  quickRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 8
  },
  quickChip: {
    padding: '2px 7px',
    fontSize: '0.66rem',
    fontWeight: 600,
    color: '#64748b',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    cursor: 'pointer',
    fontFamily: fontStack,
    letterSpacing: '0.01em',
    transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease'
  },
  quickChipActive: {
    background: '#0f172a',
    borderColor: '#0f172a',
    color: '#ffffff'
  },
  radioRow: {
    display: 'flex',
    gap: 6,
    marginBottom: 12
  },
  radioOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#475569',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: fontStack,
    transition: 'background 0.12s ease, border-color 0.12s ease, color 0.12s ease'
  },
  radioOptionActive: {
    background: '#0f172a',
    borderColor: '#0f172a',
    color: '#ffffff'
  },
  previewTabs: {
    display: 'inline-flex',
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 2,
    gap: 2
  },
  previewTab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    fontSize: '0.74rem',
    fontWeight: 600,
    color: '#475569',
    background: 'transparent',
    border: 0,
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: fontStack
  },
  previewTabActive: {
    background: '#ffffff',
    color: '#0f172a',
    boxShadow: '0 1px 1px rgba(15, 23, 42, 0.06)'
  },
  previewTabBadge: {
    minWidth: 16,
    padding: '0 5px',
    borderRadius: 999,
    background: '#e2e8f0',
    color: '#475569',
    fontSize: '0.66rem',
    fontWeight: 700,
    textAlign: 'center'
  },
  childSection: {
    marginBottom: 18
  },
  childSectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '6px 10px',
    marginBottom: 10,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderLeft: '3px solid #cbd5e1',
    borderRadius: 8,
    flexWrap: 'wrap'
  },
  childSectionTitle: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#0f172a'
  },
  childSectionSubtitle: {
    fontSize: '0.7rem',
    color: '#64748b',
    marginTop: 2
  },
  childSectionStats: {
    display: 'inline-flex',
    gap: 4,
    flexWrap: 'wrap'
  },
  childCountChip: {
    fontSize: '0.66rem',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 999,
    border: '1px solid',
    whiteSpace: 'nowrap'
  },
  childCountChipSession: {
    background: STATE_COLOR.session.bg,
    borderColor: STATE_COLOR.session.border,
    color: STATE_COLOR.session.text
  },
  childCountChipAdded: {
    background: STATE_COLOR.added.bg,
    borderColor: STATE_COLOR.added.border,
    color: STATE_COLOR.added.text
  },
  childCountChipRemoved: {
    background: STATE_COLOR.removed.bg,
    borderColor: STATE_COLOR.removed.border,
    color: STATE_COLOR.removed.text
  },
  previewBtnIcon: {
    fontSize: '0.85rem',
    lineHeight: 1
  },
  previewCard: {
    width: 'min(960px, 100%)',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 24px 48px rgba(15,23,42,0.22)',
    overflow: 'hidden',
    fontFamily: fontStack
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#ffffff'
  },
  previewSubtitle: {
    marginTop: 2,
    fontSize: '0.72rem',
    color: '#64748b'
  },
  previewSummaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    padding: '10px 16px',
    borderBottom: '1px solid #f1f5f9',
    background: '#f8fafc'
  },
  previewStat: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 80
  },
  previewStatValue: {
    fontSize: '1.05rem',
    fontWeight: 700,
    lineHeight: 1.1
  },
  previewStatLabel: {
    fontSize: '0.66rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 600,
    marginTop: 2
  },
  previewBody: {
    padding: 16,
    overflowY: 'auto',
    flex: 1
  },
  previewMonthsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))',
    gap: 14
  },
  previewLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    padding: '10px 16px',
    borderTop: '1px solid #e2e8f0',
    background: '#ffffff',
    fontSize: '0.72rem',
    color: '#475569'
  },
  legendChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6
  },
  legendDotSquare: {
    width: 12,
    height: 12,
    borderRadius: 3
  },
  legendOverlayBox: {
    width: 12,
    height: 12,
    borderRadius: 3,
    background: '#ffffff',
    outline: '2px solid #3b82f6',
    outlineOffset: '-2px',
    border: '1px solid #e2e8f0'
  },
  legendScopeLetter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 14,
    fontSize: '0.65rem',
    fontWeight: 800,
    lineHeight: 1,
    fontFamily: fontStack
  },
  monthBox: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 8,
    background: '#ffffff'
  },
  monthHeader: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 6,
    paddingLeft: 2
  },
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
    marginBottom: 4
  },
  weekdayCell: {
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#94a3b8',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2
  },
  dayCell: {
    aspectRatio: '1 / 1',
    minHeight: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: 4,
    boxSizing: 'border-box'
  },
  dayCellBlank: {
    aspectRatio: '1 / 1',
    minHeight: 26
  }
};

export default ScheduleBuilder;
